import React, { useState, useEffect } from 'react';
import {
  User,
  Shield,
  Moon,
  Sun,
  Save,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Loader2,
  Image as ImageIcon,
  LogOut,
  Bell,
  Lock,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import NotificationBell from '../../components/consultant/NotificationBell';
import EmailChangeModal from '../../components/consultant/EmailChangeModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

export default function ConsultantSettings() {
  const { theme, toggleTheme, setTheme, setThemeTransient } = useTheme();
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [existingDeletionRequest, setExistingDeletionRequest] = useState<{ status: string; requested_at: string } | null>(null);

  useEffect(() => {
    setThemeTransient('light');
  }, [setThemeTransient]);

  const [settings, setSettings] = useState({
    // Profile settings
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    title: '',

    // Notification settings
    emailNotifications: true,
    smsNotifications: false,
    opportunityAlerts: true,
    assignmentUpdates: true,
    timesheetReminders: true,

    // Privacy settings
    profileVisibility: 'public',
    showContactInfo: true,
    allowDirectMessages: true
  });

  useEffect(() => {
    if (user) {
      fetchConsultantProfile();
      fetchExistingDeletionRequest();
    }
    fetchLogo();
  }, [user?.id]);

  const fetchLogo = async () => {
    try {
      setIsLoadingLogo(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'logo_url')
          .single();

        if (error) {
          console.warn('Could not fetch logo from settings:', error.message);
          setIsLoadingLogo(false);
          return;
        }

        if (data?.value) {
          const img = new Image();
          img.src = data.value;

          img.onload = () => {
            setLogoUrl(data.value);
            setIsLoadingLogo(false);
          };

          img.onerror = () => {
            console.warn('Failed to load custom logo, using default');
            setIsLoadingLogo(false);
          };
        } else {
          setIsLoadingLogo(false);
        }
      } catch (fetchError) {
        console.warn('Network error fetching logo, using default:', fetchError);
        setIsLoadingLogo(false);
      }
    } catch (err) {
      console.error('Error fetching logo:', err);
      setIsLoadingLogo(false);
    }
  };

  const fetchConsultantProfile = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings(prev => ({
          ...prev,
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          location: data.country_region || '',
          title: data.title || '',
          profileVisibility: data.profile_visibility || 'public',
          showContactInfo: data.show_contact_info ?? true,
          allowDirectMessages: data.allow_direct_messages ?? true
        }));
      }
    } catch (error) {
      console.error('Error fetching consultant profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setSaveMessage('');
  };

  const handleSave = async () => {
    if (!user?.id) return;

    try {
      setIsSaving(true);
      setSaveMessage('');

      const { error } = await supabase
        .from('consultant_profiles')
        .update({
          first_name: settings.firstName,
          last_name: settings.lastName,
          phone: settings.phone,
          country_region: settings.location,
          title: settings.title,
          profile_visibility: settings.profileVisibility,
          show_contact_info: settings.showContactInfo,
          allow_direct_messages: settings.allowDirectMessages,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/consultant/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const fetchExistingDeletionRequest = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('account_deletion_requests')
      .select('status, requested_at')
      .eq('user_id', user.id)
      .eq('user_type', 'consultant')
      .maybeSingle();
    if (data) setExistingDeletionRequest(data);
  };

  const handleDeleteAccountRequest = async () => {
    if (!user?.id) return;
    setIsSubmittingDelete(true);
    setDeleteMessage('');
    try {
      const { error } = await supabase
        .from('account_deletion_requests')
        .insert({
          user_id: user.id,
          user_type: 'consultant',
          reason: deleteReason.trim() || null,
          status: 'pending'
        });
      if (error) throw error;
      setDeleteMessage('Your account deletion request has been submitted. An administrator will review it shortly.');
      setShowDeleteConfirm(false);
      setDeleteReason('');
      setExistingDeletionRequest({ status: 'pending', requested_at: new Date().toISOString() });
    } catch (err: any) {
      setDeleteMessage(err.message?.includes('duplicate') || err.code === '23505'
        ? 'You already have a pending deletion request.'
        : 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmittingDelete(false);
    }
  };

  const handleEmailChangeSuccess = (newEmail: string) => {
    setSettings(prev => ({ ...prev, email: newEmail }));
    setShowEmailModal(false);
    setSaveMessage('Email updated successfully!');
    setTimeout(() => setSaveMessage(''), 4000);
  };

  return (
    <>
    {showEmailModal && (
      <EmailChangeModal
        currentEmail={settings.email}
        onClose={() => setShowEmailModal(false)}
        onSuccess={handleEmailChangeSuccess}
        theme={theme}
      />
    )}
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark'
        ? 'bg-gray-900 text-white'
        : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Black Header */}
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex-1 flex items-center justify-between">
              <div className="text-white -mt-4 -mb-4 cursor-default select-none">
                {isLoadingLogo ? (
                  <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : logoUrl ? (
                  <>
                    <img
                      src="/1.png"
                      alt="Perplexity"
                      className="h-40 w-auto object-contain"
                      onError={(e) => {
                        console.warn('Error loading logo image, falling back to text');
                        e.currentTarget.style.display = 'none';
                        const textFallback = e.currentTarget.nextElementSibling as HTMLElement;
                        if (textFallback) {
                          textFallback.style.display = 'block';
                        }
                      }}
                    />
                    <div
                      className="h-32 md:h-28 flex items-center justify-center text-primary text-2xl font-bold"
                      style={{ display: 'none' }}
                    >
                      PERPLEXITY
                    </div>
                  </>
                ) : (
                  <div className="h-32 md:h-28 w-[200px] flex items-center justify-center text-primary">
                    <ImageIcon size={32} />
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg transition-colors text-white hover:bg-white/10"
                >
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                <NotificationBell variant="dark" />

                <button
                  onClick={() => navigate('/consultant/dashboard')}
                  className="p-2 rounded-lg transition-colors text-white hover:bg-white/10"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center px-4 py-2 rounded-lg transition-colors text-white hover:bg-white/10"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto p-6 pt-32">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Account Settings</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Settings Navigation */}
          <div className={`lg:col-span-1 ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          } border rounded-xl p-6`}>
            <nav className="space-y-2">
              <button
                onClick={() => document.getElementById('profile')?.scrollIntoView({ behavior: 'smooth' })}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <User className="w-5 h-5 mr-3 text-primary" />
                Profile
              </button>
              <button
                onClick={() => document.getElementById('notifications')?.scrollIntoView({ behavior: 'smooth' })}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Bell className="w-5 h-5 mr-3" />
                Notifications
              </button>
              <button
                onClick={() => document.getElementById('privacy')?.scrollIntoView({ behavior: 'smooth' })}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Shield className="w-5 h-5 mr-3" />
                Privacy
              </button>
              <button
                onClick={() => document.getElementById('security')?.scrollIntoView({ behavior: 'smooth' })}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Shield className="w-5 h-5 mr-3 text-[#40E0D0]" />
                2FA Security
              </button>
              <button
                onClick={toggleTheme}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5 mr-3" /> : <Moon className="w-5 h-5 mr-3" />}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={() => document.getElementById('delete-account')?.scrollIntoView({ behavior: 'smooth' })}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors text-red-500 hover:bg-red-50 hover:text-red-600 ${
                  theme === 'dark' ? 'hover:bg-red-900/20' : ''
                }`}
              >
                <Trash2 className="w-5 h-5 mr-3" />
                Delete Account
              </button>
            </nav>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Profile Settings */}
            <section id="profile" className={`${
              theme === 'dark' 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            } border rounded-xl p-6`}>
              <h2 className="text-xl font-semibold mb-6 flex items-center">
                <User className="w-5 h-5 mr-2 text-primary" />
                Profile Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={settings.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={settings.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Email
                  </label>
                  <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600'
                      : 'bg-gray-50 border-gray-300'
                  }`}>
                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className={`flex-1 text-sm truncate ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                      {settings.email || 'No email set'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowEmailModal(true)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#40E0D0] hover:text-[#40E0D0]/80 hover:bg-[#40E0D0]/10 rounded transition-colors flex-shrink-0"
                    >
                      <Lock className="w-3 h-3" />
                      Change
                    </button>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={settings.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Location
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={settings.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Title
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={settings.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Notification Settings */}
            <section id="notifications" className={`${
              theme === 'dark' 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            } border rounded-xl p-6`}>
              <h2 className="text-xl font-semibold mb-6 flex items-center">
                <Bell className="w-5 h-5 mr-2 text-primary" />
                Notification Preferences
              </h2>
              
              <div className="space-y-4">
                {[
                  { key: 'emailNotifications', label: 'Email Notifications', description: 'Receive notifications via email' },
                  { key: 'smsNotifications', label: 'SMS Notifications', description: 'Receive notifications via text message' },
                  { key: 'opportunityAlerts', label: 'Opportunity Alerts', description: 'Get notified about new job opportunities' },
                  { key: 'assignmentUpdates', label: 'Assignment Updates', description: 'Receive updates about your current assignments' },
                  { key: 'timesheetReminders', label: 'Timesheet Reminders', description: 'Get reminded to submit your timesheets' }
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">{item.label}</h3>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {item.description}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings[item.key as keyof typeof settings] as boolean}
                        onChange={(e) => handleInputChange(item.key, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                ))}
              </div>
            </section>

            {/* Privacy Settings */}
            <section id="privacy" className={`${
              theme === 'dark' 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            } border rounded-xl p-6`}>
              <h2 className="text-xl font-semibold mb-6 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-primary" />
                Privacy Settings
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Profile Visibility
                  </label>
                  <select
                    value={settings.profileVisibility}
                    onChange={(e) => handleInputChange('profileVisibility', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                    <option value="network">Network Only</option>
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Show Contact Information</h3>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Allow others to see your contact details
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.showContactInfo}
                      onChange={(e) => handleInputChange('showContactInfo', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Allow Direct Messages</h3>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Let recruiters and clients message you directly
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.allowDirectMessages}
                      onChange={(e) => handleInputChange('allowDirectMessages', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div className="flex flex-col items-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                  {saveMessage && (
                    <div className={`text-sm font-medium ${
                      saveMessage.includes('success') || saveMessage.includes('updated') ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {saveMessage}
                    </div>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={isSaving || isLoading}
                    className="flex items-center px-6 py-3 bg-primary text-black font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </section>

            {/* Delete Account Section */}
            <section id="delete-account" className={`${
              theme === 'dark'
                ? 'bg-gray-800 border-red-900/50'
                : 'bg-white border-red-200'
            } border rounded-xl p-6`}>
              <h2 className="text-xl font-semibold mb-2 flex items-center text-red-500">
                <Trash2 className="w-5 h-5 mr-2" />
                Delete Account
              </h2>
              <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Request the permanent deletion of your account and all associated data. This action cannot be undone once processed by an administrator.
              </p>

              {existingDeletionRequest ? (
                <div className={`p-4 rounded-lg border ${
                  existingDeletionRequest.status === 'pending'
                    ? theme === 'dark' ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
                    : existingDeletionRequest.status === 'dismissed'
                    ? theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    : theme === 'dark' ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      existingDeletionRequest.status === 'pending' ? 'text-yellow-500'
                      : existingDeletionRequest.status === 'dismissed' ? 'text-gray-400'
                      : 'text-red-500'
                    }`} />
                    <div>
                      <p className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {existingDeletionRequest.status === 'pending' && 'Deletion Request Pending'}
                        {existingDeletionRequest.status === 'approved' && 'Deletion Request Approved'}
                        {existingDeletionRequest.status === 'dismissed' && 'Deletion Request Dismissed'}
                      </p>
                      <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {existingDeletionRequest.status === 'pending' && 'Your request has been submitted and is awaiting review by an administrator.'}
                        {existingDeletionRequest.status === 'approved' && 'Your account deletion has been approved and will be processed.'}
                        {existingDeletionRequest.status === 'dismissed' && 'Your deletion request was dismissed. Contact support if you have questions.'}
                      </p>
                      <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                        Requested: {new Date(existingDeletionRequest.requested_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                </div>
              ) : showDeleteConfirm ? (
                <div className={`p-5 rounded-lg border ${
                  theme === 'dark' ? 'bg-gray-900 border-red-800' : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>
                      This will send a deletion request to the admin. Your account will remain active until an administrator processes the request.
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Reason for deletion (optional)
                    </label>
                    <textarea
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      rows={3}
                      placeholder="Tell us why you want to delete your account..."
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none ${
                        theme === 'dark'
                          ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                  {deleteMessage && (
                    <p className="text-sm text-red-500 mb-4">{deleteMessage}</p>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={handleDeleteAccountRequest}
                      disabled={isSubmittingDelete}
                      className="flex items-center px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmittingDelete ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                      ) : (
                        <><Trash2 className="w-4 h-4 mr-2" />Submit Request</>
                      )}
                    </button>
                    <button
                      onClick={() => { setShowDeleteConfirm(false); setDeleteReason(''); setDeleteMessage(''); }}
                      className={`px-4 py-2 font-medium rounded-lg transition-colors ${
                        theme === 'dark'
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {deleteMessage && (
                    <p className={`text-sm mb-4 ${deleteMessage.includes('submitted') ? 'text-green-500' : 'text-red-500'}`}>
                      {deleteMessage}
                    </p>
                  )}
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center px-5 py-2.5 border border-red-500 text-red-500 font-semibold rounded-lg hover:bg-red-500 hover:text-white transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Request Account Deletion
                  </button>
                </>
              )}
            </section>

            {/* Two-Factor Authentication Section */}
            <section id="security" className={`${
              theme === 'dark'
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
            } border rounded-xl p-6`}>
              <h2 className="text-xl font-semibold mb-6 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-primary" />
                Two-Factor Authentication
              </h2>

              <div className="space-y-6">
                <div className={`p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
                }`}>
                  <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                    <strong>Important:</strong> Two-factor authentication (2FA) adds an extra layer of security to your account by requiring a code from your phone in addition to your password.
                  </p>
                </div>

                <div className={`p-6 rounded-lg border-2 ${
                  theme === 'dark' ? 'border-[#40E0D0]/30 bg-gray-900/50' : 'border-[#40E0D0] bg-gradient-to-br from-gray-50 to-white'
                }`}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center text-[#40E0D0]">
                    <Shield className="w-5 h-5 mr-2" />
                    How to Change Your Email
                  </h3>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#40E0D0] text-white rounded-full flex items-center justify-center font-bold text-sm">
                        1
                      </div>
                      <div className="flex-1">
                        <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Verify Your Current Password
                        </p>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          Click "Change Email" and enter your current account password to confirm your identity before making any changes
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#40E0D0] text-white rounded-full flex items-center justify-center font-bold text-sm">
                        2
                      </div>
                      <div className="flex-1">
                        <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Enter Your New Email Address
                        </p>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          Type the new email address you want to use for your account going forward
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#40E0D0] text-white rounded-full flex items-center justify-center font-bold text-sm">
                        3
                      </div>
                      <div className="flex-1">
                        <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Scan the New QR Code
                        </p>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          A new QR code will be generated tied to your new email. Open Google Authenticator, add a new account, and scan the QR code — or enter the manual entry key if you prefer
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#40E0D0] text-white rounded-full flex items-center justify-center font-bold text-sm">
                        4
                      </div>
                      <div className="flex-1">
                        <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Enter the Verification Code
                        </p>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          Google Authenticator will display a 6-digit code for your new entry. Enter it to confirm the change and complete your email update
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className={`mt-6 p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                  }`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                      <strong>After Changing:</strong> Your Google Authenticator will be automatically re-linked to your new email. Use your new email address to log in going forward.
                    </p>
                  </div>
                </div>

              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}